import { useState, useEffect, useCallback } from "react";
import { RenderMarkdownWithLatex } from "../../utils/utils";

const apiUrl = process.env.REACT_APP_API_SOURCE;
const token = localStorage.getItem("token");

// ─── Shared card ────────────────────────────────────────────────────────────

const MaturaProblemCard = ({ problem }) => {
  const [showSolution, setShowSolution] = useState(false);
  const [showCriteria, setShowCriteria] = useState(false);

  return (
    <div className={`border rounded-lg p-4 sm:p-6 mb-4 shadow-sm bg-white relative ${problem.is_excluded_2025 ? "border-red-300" : "border-gray-300"}`}>
      <div className="mb-3 pb-2 border-b border-gray-200">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400 mt-0.5">
          <p>ID: <span className="font-bold text-gray-700">{problem.matura_problem_id}</span></p>
          <p className="font-bold text-gray-600">{problem.matura_problem_points} pkt</p>
          <p className={`font-medium ${problem.matura_problem_is_open ? "text-amber-600" : "text-blue-600"}`}>
            {problem.matura_problem_is_open ? "Otwarte" : "Zamknięte"}
          </p>
          <p className="font-bold text-purple-600">{problem.exam_type}</p>
          {problem.is_excluded_2025 && <p className="font-bold text-red-600">⚠ Wykluczone 2025</p>}
        </div>
        {problem.matura_problem_source && (
          <p className="text-xs text-gray-500 mt-1">{problem.matura_problem_source}</p>
        )}
        {problem.is_excluded_2025 && problem.curriculum_2025_rationale && (
          <p className="text-xs text-red-500 mt-1 italic">Powód: {problem.curriculum_2025_rationale}</p>
        )}
      </div>

      <div className="mb-4 text-gray-900 text-[1.0rem] leading-relaxed">
        <RenderMarkdownWithLatex text={problem.matura_problem_question || "Brak treści."} />
      </div>

      {problem.reference_solution && (
        <div className="mt-3">
          <button onClick={() => setShowSolution(!showSolution)}
            className="text-xs uppercase font-bold text-blue-600 hover:text-blue-800 mb-2 flex items-center gap-2">
            {showSolution ? "▼ Ukryj Rozwiązanie" : "▶ Pokaż Rozwiązanie"}
          </button>
          {showSolution && (
            <div className="border border-dashed border-blue-200 p-3 rounded bg-blue-50/20 text-sm text-gray-700 leading-relaxed">
              <RenderMarkdownWithLatex text={problem.reference_solution} />
            </div>
          )}
        </div>
      )}

      {problem.grading_criteria && (
        <div className="mt-2">
          <button onClick={() => setShowCriteria(!showCriteria)}
            className="text-xs uppercase font-bold text-green-600 hover:text-green-800 mb-2 flex items-center gap-2">
            {showCriteria ? "▼ Ukryj Kryteria" : "▶ Pokaż Kryteria"}
          </button>
          {showCriteria && (
            <div className="border border-dashed border-green-200 p-3 rounded bg-green-50/20 text-sm text-gray-700 leading-relaxed">
              <RenderMarkdownWithLatex text={problem.grading_criteria} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Tab 1: Browse by source ─────────────────────────────────────────────────

const BrowseBySourceTab = () => {
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState("");
  const [examType, setExamType] = useState("");
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    fetch(`${apiUrl}matura-problems/sources/`, { headers: { Authorization: `Token ${token}` } })
      .then(r => r.json())
      .then(data => setSources(Array.isArray(data) ? data : []))
      .catch(() => setSources([]));
  }, []);

  const fetchProblems = useCallback(async (source, type, pageNum) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: pageNum });
      if (source) params.set("source", source);
      if (type) params.set("exam_type", type);
      const res = await fetch(`${apiUrl}matura-problems/?${params}`, {
        headers: { Authorization: `Token ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProblems(data.results ?? []);
      setCount(data.count ?? 0);
      setHasNext(!!data.next);
      setHasPrev(!!data.previous);
    } catch {
      setError("Nie udało się pobrać zadań.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFetch = () => { setPage(1); fetchProblems(selectedSource, examType, 1); };

  useEffect(() => {
    if (page > 1) fetchProblems(selectedSource, examType, page);
  }, [page]); // eslint-disable-line

  return (
    <>
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <select value={selectedSource} onChange={e => setSelectedSource(e.target.value)}
            className="border border-gray-300 rounded p-2 min-w-[260px] focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">--- Wybierz Egzamin ---</option>
            {sources.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={examType} onChange={e => setExamType(e.target.value)}
            className="border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Wszystkie typy</option>
            <option value="PP">PP</option>
            <option value="PR">PR</option>
            <option value="E8">E8</option>
          </select>
          <button onClick={handleFetch} disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded font-semibold hover:bg-blue-700 disabled:opacity-50">
            {loading ? "Ładowanie..." : "Pobierz"}
          </button>
        </div>
      </div>

      {error && <p className="text-red-600 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">{error}</p>}
      {!loading && problems.length > 0 && (
        <p className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm">
          Wyświetlono: <b>{problems.length}</b> z <b>{count}</b> zadań
        </p>
      )}
      {loading && <p className="text-blue-600 text-center py-4">Ładowanie...</p>}
      {!loading && problems.map((p, i) => (
        <MaturaProblemCard key={p.matura_problem_id} problem={p} index={(page - 1) * 50 + i} total={count} />
      ))}
      {!loading && problems.length === 0 && !error && (
        <p className="text-gray-500 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          Wybierz filtry i kliknij "Pobierz".
        </p>
      )}
      {(hasNext || hasPrev) && (
        <div className="flex items-center justify-between mt-4">
          <button disabled={!hasPrev} onClick={() => setPage(v => v - 1)}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded font-semibold hover:bg-gray-300 disabled:opacity-40">
            ← Poprzednia
          </button>
          <span className="text-sm text-gray-500">Strona {page}</span>
          <button disabled={!hasNext} onClick={() => setPage(v => v + 1)}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded font-semibold hover:bg-gray-300 disabled:opacity-40">
            Następna →
          </button>
        </div>
      )}
    </>
  );
};

// ─── Tab 2: XLSX Mapping ──────────────────────────────────────────────────────

const LEVEL_COLORS = {
  "★": "bg-green-100 text-green-700",
  "★★": "bg-yellow-100 text-yellow-700",
  "★★★": "bg-red-100 text-red-700",
};

const TemplateBlock = ({ tpl }) => {
  const [open, setOpen] = useState(false);
  const hasMissing = tpl.missing_ids.length > 0;

  return (
    <div className={`border rounded-lg mb-2 ${hasMissing ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"}`}>
      <button onClick={() => setOpen(v => !v)}
        className="w-full flex items-start gap-3 p-3 text-left hover:bg-gray-50 rounded-lg">
        <span className="mt-0.5 text-gray-400 text-sm">{open ? "▼" : "▶"}</span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-0.5">
            <span className={`text-xs font-bold px-2 py-0.5 rounded ${LEVEL_COLORS[tpl.poziom] || "bg-gray-100 text-gray-600"}`}>
              {tpl.poziom || "?"}
            </span>
            {hasMissing && (
              <span className="text-xs font-bold px-2 py-0.5 rounded bg-red-200 text-red-700">
                ⚠ {tpl.missing_ids.length} brak w DB
              </span>
            )}
            <span className="text-xs text-gray-400">{tpl.ids.length} zadań · ID: {tpl.ids.join(", ")}</span>
          </div>
          <p className="text-sm font-semibold text-gray-800">{tpl.szablon}</p>
          {tpl.podkategoria && <p className="text-xs text-gray-500 mt-0.5">{tpl.podkategoria}</p>}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100">
          {hasMissing && (
            <p className="text-sm text-red-600 mb-3 font-medium">
              Brakujące ID w DB: {tpl.missing_ids.join(", ")}
            </p>
          )}
          {tpl.problems.length === 0 && !hasMissing && (
            <p className="text-sm text-gray-400">Brak danych.</p>
          )}
          {tpl.problems.map(p => (
            <MaturaProblemCard key={p.matura_problem_id} problem={p} />
          ))}
        </div>
      )}
    </div>
  );
};

const CategoryBlock = ({ cat }) => {
  const [open, setOpen] = useState(false);
  const totalProblems = cat.templates.reduce((s, t) => s + t.ids.length, 0);
  const missingCount = cat.templates.reduce((s, t) => s + t.missing_ids.length, 0);

  return (
    <div className="mb-3 border border-gray-300 rounded-xl overflow-hidden shadow-sm">
      <button onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 text-white text-left">
        <div className="flex items-center gap-3">
          <span>{open ? "▼" : "▶"}</span>
          <span className="font-bold text-base">{cat.kategoria}</span>
          {missingCount > 0 && (
            <span className="text-xs font-bold bg-red-500 text-white px-2 py-0.5 rounded">
              ⚠ {missingCount} brak
            </span>
          )}
        </div>
        <span className="text-sm text-gray-400">{cat.templates.length} szablonów · {totalProblems} zadań</span>
      </button>

      {open && (
        <div className="p-3 bg-gray-50">
          {cat.templates.map((tpl, i) => <TemplateBlock key={i} tpl={tpl} />)}
        </div>
      )}
    </div>
  );
};

const XlsxMappingTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}matura-problems/xlsx-mapping/`, {
        headers: { Authorization: `Token ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(`Błąd: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const filtered = !data ? [] : data.map(cat => ({
    ...cat,
    templates: cat.templates.filter(t => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        t.szablon.toLowerCase().includes(q) ||
        t.podkategoria.toLowerCase().includes(q) ||
        t.ids.some(id => String(id).includes(q))
      );
    }),
  })).filter(cat => cat.templates.length > 0);

  const totalIds = data ? data.reduce((s, c) => s + c.templates.reduce((ss, t) => ss + t.ids.length, 0), 0) : 0;
  const totalMissing = data ? data.reduce((s, c) => s + c.templates.reduce((ss, t) => ss + t.missing_ids.length, 0), 0) : 0;

  return (
    <>
      <div className="bg-white p-4 rounded-lg shadow mb-4 flex flex-wrap items-center gap-4">
        <button onClick={load} disabled={loading}
          className="bg-indigo-600 text-white px-5 py-2 rounded font-semibold hover:bg-indigo-700 disabled:opacity-50">
          {loading ? "Ładowanie..." : data ? "Odśwież" : "Załaduj mapping z XLSX"}
        </button>
        {data && (
          <>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Szukaj szablon / ID..."
              className="border border-gray-300 rounded p-2 min-w-[220px] focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
            <div className="flex gap-4 text-sm">
              <span className="text-gray-600"><b>{totalIds}</b> zadań w xlsx</span>
              {totalMissing > 0
                ? <span className="text-red-600 font-bold">⚠ {totalMissing} brak w DB</span>
                : <span className="text-green-600 font-bold">✓ Wszystkie w DB</span>
              }
            </div>
          </>
        )}
      </div>

      {error && <p className="text-red-600 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">{error}</p>}

      {!data && !loading && (
        <p className="text-gray-500 p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm">
          Kliknij "Załaduj" żeby pobrać mapping z pliku xlsx i zobaczyć rzeczywiste zadania z bazy.
        </p>
      )}

      {filtered.map((cat, i) => <CategoryBlock key={i} cat={cat} />)}
    </>
  );
};


// ─── Tab 3: AI Problem Staging ────────────────────────────────────────────────

const AIProblemStagingCard = ({ problem }) => {
  const [showSolution, setShowSolution] = useState(false);
  const [showCriteria, setShowCriteria] = useState(false);

  return (
    <div className={`border rounded-lg p-4 sm:p-6 mb-4 shadow-sm bg-white relative border-gray-300`}>
      <div className="mb-3 pb-2 border-b border-gray-200">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400 mt-0.5">
          <p>ID: <span className="font-bold text-gray-700">{problem.id}</span></p>
          <p>Status: <span className="font-bold text-gray-700">{problem.status}</span></p>
          <p className={`font-medium ${problem.is_open ? "text-amber-600" : "text-blue-600"}`}>
            {problem.is_open ? "Otwarte" : "Zamknięte"}
          </p>
        </div>
      </div>

      <div className="mb-4 text-gray-900 text-[1.0rem] leading-relaxed">
        <RenderMarkdownWithLatex text={problem.raw_question || "Brak treści."} />
      </div>

      {problem.raw_solution && (
        <div className="mt-3">
          <button onClick={() => setShowSolution(!showSolution)}
            className="text-xs uppercase font-bold text-blue-600 hover:text-blue-800 mb-2 flex items-center gap-2">
            {showSolution ? "▼ Ukryj Rozwiązanie" : "▶ Pokaż Rozwiązanie"}
          </button>
          {showSolution && (
            <div className="border border-dashed border-blue-200 p-3 rounded bg-blue-50/20 text-sm text-gray-700 leading-relaxed">
              <RenderMarkdownWithLatex text={problem.raw_solution} />
            </div>
          )}
        </div>
      )}

      {problem.grading_criteria && (
        <div className="mt-2">
          <button onClick={() => setShowCriteria(!showCriteria)}
            className="text-xs uppercase font-bold text-green-600 hover:text-green-800 mb-2 flex items-center gap-2">
            {showCriteria ? "▼ Ukryj Kryteria" : "▶ Pokaż Kryteria"}
          </button>
          {showCriteria && (
            <div className="border border-dashed border-green-200 p-3 rounded bg-green-50/20 text-sm text-gray-700 leading-relaxed">
              <RenderMarkdownWithLatex text={problem.grading_criteria} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AIStagingTab = () => {
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [count, setCount] = useState(0);

  const fetchProblems = useCallback(async (pageNum) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: pageNum });
      const res = await fetch(`${apiUrl}ai-problem-staging/?${params}`, {
        headers: { Authorization: `Token ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProblems(data.results ?? []);
      setCount(data.count ?? 0);
      setHasNext(!!data.next);
      setHasPrev(!!data.previous);
    } catch {
      setError("Nie udało się pobrać zadań AI.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProblems(page);
  }, [page, fetchProblems]);

  return (
    <>
      {error && <p className="text-red-600 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">{error}</p>}
      {!loading && problems.length > 0 && (
        <p className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm">
          Wyświetlono: <b>{problems.length}</b> z <b>{count}</b> zadań
        </p>
      )}
      {loading && <p className="text-blue-600 text-center py-4">Ładowanie...</p>}
      {!loading && problems.map((p, i) => (
        <AIProblemStagingCard key={p.id} problem={p} index={(page - 1) * 50 + i} total={count} />
      ))}
      {!loading && problems.length === 0 && !error && (
        <p className="text-gray-500 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          Brak zadań do wyświetlenia.
        </p>
      )}
      {(hasNext || hasPrev) && (
        <div className="flex items-center justify-between mt-4">
          <button disabled={!hasPrev} onClick={() => setPage(v => v - 1)}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded font-semibold hover:bg-gray-300 disabled:opacity-40">
            ← Poprzednia
          </button>
          <span className="text-sm text-gray-500">Strona {page}</span>
          <button disabled={!hasNext} onClick={() => setPage(v => v + 1)}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded font-semibold hover:bg-gray-300 disabled:opacity-40">
            Następna →
          </button>
        </div>
      )}
    </>
  );
};


// ─── Main page ────────────────────────────────────────────────────────────────


const TABS = [
  { id: "source", label: "Przeglądaj po egzaminie" },
  { id: "xlsx", label: "XLSX Mapping" },
  { id: "ai_staging", label: "AI Staging" },
];

const MaturaProblemsAdminPage = () => {
  const [tab, setTab] = useState("xlsx");

  return (
    <div className="container mx-auto px-4 py-6 bg-gray-100 min-h-screen">
      <h1 className="text-2xl font-bold mb-4 text-gray-800">Zadania Maturalne</h1>

      <div className="flex gap-1 mb-6 bg-white p-1 rounded-lg shadow w-fit">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
              tab === t.id ? "bg-gray-800 text-white" : "text-gray-600 hover:bg-gray-100"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "source" && <BrowseBySourceTab />}
      {tab === "xlsx" && <XlsxMappingTab />}
      {tab === "ai_staging" && <AIStagingTab />}
    </div>
  );
};

export default MaturaProblemsAdminPage;
